import { Plus } from 'lucide-react';
import { useIntl } from 'react-intl';
import { Button } from '@hqo/ui-atoms';

import { ACTIVITY_TYPE_CONFIG, ActivityFilter } from 'app/shared/consts/activity';

interface ActivityEndCtaProps {
  /** Picks the vertical's copy, or the all-verticals default. */
  filter: ActivityFilter;
  onCta: () => void;
}

interface EndCtaCopy {
  promptId: string;
  ctaId: string;
}

/**
 * `all` has no vertical to read config from, and its CTA leaves the feed entirely rather than
 * creating anything — the reader who has run out of everything is looking for the building, not for
 * one more form.
 */
const copyFor = (filter: ActivityFilter): EndCtaCopy => {
  if (filter === 'all') {
    return { promptId: 'activity.endCta.all.prompt', ctaId: 'activity.empty.all.cta' };
  }

  const config = ACTIVITY_TYPE_CONFIG[filter];
  return { promptId: config.endCtaPromptId, ctaId: config.emptyCtaId };
};

/**
 * The offer at the end of the list, once there is nothing further to load.
 *
 * Replaces the floating `+`, which sat over the last card at every scroll position and said only
 * "+". This says what it is for, and appears at the one moment it is the obvious next thing: the
 * reader has reached the bottom and run out of records.
 *
 * Dashed rather than solid so it reads as an offer at the end of the list rather than one more
 * record in it.
 */
export const ActivityEndCta = ({ filter, onCta }: ActivityEndCtaProps): JSX.Element => {
  const intl = useIntl();
  const { promptId, ctaId } = copyFor(filter);

  return (
    <div
      data-testid="activity-end-cta"
      className="mt-3 flex flex-col items-center gap-4 rounded-2xl border border-dashed border-border px-6 py-6 text-center"
    >
      <p className="text-sm leading-6 text-muted-foreground">{intl.formatMessage({ id: promptId })}</p>
      <Button
        type="button"
        variant="outline"
        onClick={onCta}
        data-testid="activity-end-cta-button"
        /*
         * The BUILDING's colour, per the design, where this read plain black before —
         * `--foreground` is `--system-black`, and the empty state's solid CTA beside it has been
         * `bg-primary` all along, so the two disagreed.
         *
         * Both hover states are overridden, not just the resting ones: ui-atoms' `outline` variant
         * carries `hover:bg-accent hover:text-accent-foreground`, and that foreground is the same
         * `--system-black` — so fixing only the resting colour would have left it turning black
         * under the pointer, which is when a reader is looking hardest at it.
         */
        className="h-auto whitespace-nowrap rounded-full border-primary px-[22px] py-3 text-sm font-semibold text-primary shadow-none hover:bg-primary/10 hover:text-primary"
      >
        <Plus size={18} strokeWidth={2.5} aria-hidden />
        {intl.formatMessage({ id: ctaId })}
      </Button>
    </div>
  );
};
